/**
 * 租户管理模块设计规范 - Design Tokens
 * 基于任务文档 #3786 的设计规范
 */

export const DesignTokens = {
  // 配色方案
  colors: {
    // 主色调
    primary: '#1890ff',
    primaryLight: '#40a9ff',
    primaryDark: '#096dd9',

    // 功能色
    success: '#52c41a',
    warning: '#faad14',
    error: '#f5222d',
    info: '#1890ff',

    // 中性色
    gray1: '#ffffff',
    gray2: '#fafafa',
    gray3: '#f5f5f5',
    gray4: '#e8e8e8',
    gray5: '#d9d9d9',
    gray6: '#bfbfbf',
    gray7: '#8c8c8c',
    gray8: '#595959',
    gray9: '#262626',
  },

  // 间距系统
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
  },

  // 圆角
  borderRadius: {
    sm: 2,
    md: 4,
    lg: 8,
  },

  // 阴影
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,0.05)',
    md: '0 2px 8px rgba(0,0,0,0.1)',
    lg: '0 4px 16px rgba(0,0,0,0.12)',
  },

  // 字体规范
  typography: {
    h1: { fontSize: 24, lineHeight: '32px', fontWeight: 600 },
    h2: { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
    h3: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
    h4: { fontSize: 14, lineHeight: '22px', fontWeight: 600 },
    bodyLarge: { fontSize: 16, lineHeight: '24px', fontWeight: 400 },
    bodyRegular: { fontSize: 14, lineHeight: '22px', fontWeight: 400 },
    bodySmall: { fontSize: 12, lineHeight: '20px', fontWeight: 400 },
    caption: { fontSize: 12, lineHeight: '20px', fontWeight: 400 },
    label: { fontSize: 14, lineHeight: '22px', fontWeight: 500 },
  },

  // 容量使用颜色映射
  capacityColors: {
    safe: '#52c41a',    // 0-60%
    warning: '#faad14',  // 61-80%
    danger: '#f5222d',   // 81-100%
  },
};

// 根据容量百分比获取颜色
export const getCapacityColor = (percentage: number): string => {
  if (percentage <= 60) return DesignTokens.capacityColors.safe;
  if (percentage <= 80) return DesignTokens.capacityColors.warning;
  return DesignTokens.capacityColors.danger;
};

// 导出示例组件
import React from 'react';
import { Card, Typography, Space, Tag } from 'antd';

const { Title, Paragraph } = Typography;

export const DesignSystemPreview: React.FC = () => {
  return (
    <div style={{ padding: DesignTokens.spacing.xl, background: DesignTokens.colors.gray1 }}>
      <Title level={2} style={DesignTokens.typography.h2}>
        租户管理设计系统
      </Title>

      {/* 配色方案 */}
      <Card title="配色方案" style={{ marginBottom: DesignTokens.spacing.xl }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Paragraph strong>主色调</Paragraph>
            <Space>
              <div style={{
                width: 80,
                height: 40,
                background: DesignTokens.colors.primary,
                borderRadius: DesignTokens.borderRadius.md,
              }} />
              <span>Primary: {DesignTokens.colors.primary}</span>
            </Space>
          </div>

          <div>
            <Paragraph strong>功能色</Paragraph>
            <Space>
              {Object.entries({
                成功: DesignTokens.colors.success,
                警告: DesignTokens.colors.warning,
                错误: DesignTokens.colors.error,
              }).map(([name, color]) => (
                <div key={name}>
                  <div style={{
                    width: 60,
                    height: 40,
                    background: color,
                    borderRadius: DesignTokens.borderRadius.md,
                    marginBottom: 4,
                  }} />
                  <div style={{ fontSize: 12 }}>{name}</div>
                </div>
              ))}
            </Space>
          </div>

          <div>
            <Paragraph strong>中性色</Paragraph>
            <Space>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                const key = `gray${i}` as keyof typeof DesignTokens.colors;
                return (
                  <div key={i} style={{
                    width: 40,
                    height: 40,
                    background: DesignTokens.colors[key],
                    border: `1px solid ${DesignTokens.colors.gray4}`,
                    borderRadius: DesignTokens.borderRadius.sm,
                  }} />
                );
              })}
            </Space>
          </div>
        </Space>
      </Card>

      {/* 容量颜色示例 */}
      <Card title="容量使用颜色" style={{ marginBottom: DesignTokens.spacing.xl }}>
        <Space>
          {[30, 70, 90].map((percent) => (
            <Tag key={percent} color={getCapacityColor(percent)}>
              {percent}% - {percent <= 60 ? '安全' : percent <= 80 ? '警告' : '危险'}
            </Tag>
          ))}
        </Space>
      </Card>

      {/* 间距系统 */}
      <Card title="间距系统">
        <Space direction="vertical">
          {Object.entries(DesignTokens.spacing).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ width: 60 }}>{key}:</span>
              <div style={{
                width: value,
                height: 20,
                background: DesignTokens.colors.primary,
                marginLeft: 8,
              }} />
              <span style={{ marginLeft: 8 }}>{value}px</span>
            </div>
          ))}
        </Space>
      </Card>
    </div>
  );
};
